/**
 * MainContent - Standard main content section for event/membership pages
 *
 * Renders description, restrictions, location, alerts, and optional header content.
 * Used for non-attraction pages.
 *
 * @param headerContent - Optional slot for badges or other header content (Hero uses this)
 */
import { useState, useEffect, ReactNode } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { gridGaps } from "@shared/theme/props/grid";
import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import TicketsNotAvailableText from "@app-contexts/public/features/tickets/components/TicketsNotAvailableText/TicketsNotAvailableText.web";
import type { TicketTypeSerializer } from "@shared/modules/events/types/public";
import type { PageData } from "../../../adapters/types";
import { RegionNotAllowedText } from "../../../components/shared/RegionNotAllowedText.web";
import { DetailPageInfo } from "../../../components/shared/DetailPageInfo.web";

interface MainContentProps {
  data: PageData;
  headerContent?: ReactNode;
}

export function MainContent({
  data,
  headerContent,
}: MainContentProps): JSX.Element {
  const { t } = useTranslation("purchases");
  const { header, mainContent, ui, rawData, pageType } = data;

  // Build venue info for DetailPageInfo
  const venueInfo = {
    venueId: mainContent.venueInfo.id,
    venueSlug: mainContent.venueInfo.slug ?? "",
    venueName: mainContent.venueInfo.name ?? "",
    venueDescription: mainContent.venueInfo.description,
    venuePrivate: mainContent.venueInfo.isPrivate,
  };

  // Check for alerts
  const hasRegionAlert = mainContent.alerts.some(
    (alert) => alert.type === "region"
  );
  const hasUnavailableAlert = mainContent.alerts.some(
    (alert) => alert.type === "unavailable"
  );

  // Get unavailable text data from raw event data
  // Hydration-safe date comparison - server renders as false to avoid mismatch
  const [pastEvent, setPastEvent] = useState(false);
  useEffect(() => {
    if (rawData.event?.ends_on) {
      setPastEvent(new Date(rawData.event.ends_on) < new Date());
    }
  }, [rawData.event?.ends_on]);
  const soldOutEvent = rawData.event?.sold_out ?? false;
  const ticketsNotRequired = rawData.event?.no_ticket_types ?? false;
  const hasWaitlistItems =
    rawData.event?.ticket_types?.some(
      (tt: TicketTypeSerializer) =>
        tt.waitlist?.status === "active" &&
        (tt.stats?.quantity_sold ?? 0) >= (tt.inventory ?? 0)
    ) ?? false;
  const allowedRegionsText = rawData.event?.allowed_regions?.join(", ") ?? "";

  return (
    <Flex
      id="additional-info"
      direction="column"
      scrollMarginTop={ui.scrollMargin}
      gap={gridGaps.default}
    >
      {/* Optional header content slot (badges for Hero layout) */}
      {headerContent}

      {/* Region not allowed alert */}
      {hasRegionAlert && pageType === "event" && (
        <DetailCardContainer>
          <RegionNotAllowedText allowedRegions={allowedRegionsText} />
        </DetailCardContainer>
      )}

      {/* Tickets not available alert */}
      {hasUnavailableAlert && pageType === "event" && (
        <DetailCardContainer>
          <TicketsNotAvailableText
            pastEvent={pastEvent}
            soldOutEvent={soldOutEvent}
            ticketsNotRequired={ticketsNotRequired}
            waitlistedEvent={hasWaitlistItems}
            customTicketMessage={rawData.event?.no_ticket_types_message ?? ""}
          />
        </DetailCardContainer>
      )}

      {/* Main content info - Description, restrictions, etc. */}
      <DetailPageInfo
        infoHeader={t("Description")}
        infoText={mainContent.description ?? ""}
        infoRestrictions={mainContent.restrictions}
        refundPolicyText={mainContent.refundPolicy ?? ""}
        openContactDetailsModal={header.organizer.onContact}
        isLoading={ui.isLoading}
        venueInfo={venueInfo}
        eventId={mainContent.locationInfo?.id}
        accommodationUrl={mainContent.locationInfo?.accommodationUrl}
        isAttractionPage={false}
      />
    </Flex>
  );
}
